<template>
    <div class="country-container" @click="openSelect">
        <ion-icon v-if="!Capacitor.isNativePlatform()" :src="flagSrc" :class="colors" class="country-dropdown-icon" :key="iconKey"/>
        <span v-else>{{ country.flag }}</span> <!-- emoji flag on mobile -->
        <ion-select
            ref="selectEl"
            :cancel-text="$t('button.cancel')"
            :interface="isApp() ? ION_SELECT_INTERFACE.ALERT : ION_SELECT_INTERFACE.ACTION_SHEET"
            v-model="country"
            @ion-change="() => {} /*selectChangeFunction*/"
            class="country-dropdown"
            :class="colors"
            :selected-text="`+${country.dialCode}`"
            mode="ios"
            style="color: black;"
        >
            <ion-select-option v-for="country in europeanCountries" :key="country.iso2" :value="country">
                <span
                    v-if="Capacitor.isNativePlatform()"
                    v-html="country.flag"
                />
                {{ country.name }} (+{{ country.dialCode }})
            </ion-select-option>
        </ion-select>
    </div>
    <input-simple
        :modelValue="phoneInput"
        @update:modelValue="onPhoneInput"
        class="grey4-grey4-background inherit-background purple-purple-color unset-max-width international"
        :placeholder="$t('placeholder.phone')"
        :type="INPUT_TYPE.TEL"
        :inputmode="INPUT_MODE.TEL"
        input-class="phone-input"
        :validate-option="{ name: 'phone', label: $t('label.phone') }"
    />
</template>

<script setup lang="ts">
import { IonIcon, IonSelect, IonSelectOption } from "@ionic/vue";
import { computed, ref, watch } from "vue";
import { Capacitor } from "@capacitor/core";

import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { INPUT_MODE, INPUT_TYPE, ION_SELECT_INTERFACE } from "@/types/enum/ionicEnum";
import { isApp } from "@/functions/enviroment";
import InputSimple from "@/components/input/InputSimple.vue";

const props = withDefaults(
    defineProps<{
        initCoutry: string
        phone: string
        colors?: string
    }>(),
    {
        colors: 'coral-light-color'
    }
)

const emit = defineEmits<{
    (e: 'update:phone', value: string): void
    (e: 'valid', isValid: boolean): void
}>()

const defaultCountry = { name: "Germany", iso2: "de", dialCode: "49", flag: "🇩🇪" }
const europeanCountries = [
    { name: "Albania", iso2: "al", dialCode: "355", flag: "🇦🇱" },
    { name: "Andorra", iso2: "ad", dialCode: "376", flag: "🇦🇩" },
    { name: "Austria", iso2: "at", dialCode: "43", flag: "🇦🇹" },
    { name: "Belgium", iso2: "be", dialCode: "32", flag: "🇧🇪" },
    { name: "Bosnia and Herzegovina", iso2: "ba", dialCode: "387", flag: "🇧🇦" },
    { name: "Bulgaria", iso2: "bg", dialCode: "359", flag: "🇧🇬" },
    { name: "Croatia", iso2: "hr", dialCode: "385", flag: "🇭🇷" },
    { name: "Cyprus", iso2: "cy", dialCode: "357", flag: "🇨🇾" },
    { name: "Czech Republic", iso2: "cz", dialCode: "420", flag: "🇨🇿" },
    { name: "Denmark", iso2: "dk", dialCode: "45", flag: "🇩🇰" },
    { name: "Estonia", iso2: "ee", dialCode: "372", flag: "🇪🇪" },
    { name: "Finland", iso2: "fi", dialCode: "358", flag: "🇫🇮" },
    { name: "France", iso2: "fr", dialCode: "33", flag: "🇫🇷" },
    { name: "Germany", iso2: "de", dialCode: "49", flag: "🇩🇪" },
    { name: "Greece", iso2: "gr", dialCode: "30", flag: "🇬🇷" },
    { name: "Hungary", iso2: "hu", dialCode: "36", flag: "🇭🇺" },
    { name: "Iceland", iso2: "is", dialCode: "354", flag: "🇮🇸" },
    { name: "Ireland", iso2: "ie", dialCode: "353", flag: "🇮🇪" },
    { name: "Italy", iso2: "it", dialCode: "39", flag: "🇮🇹" },
    { name: "Kosovo", iso2: "xk", dialCode: "383", flag: "🇽🇰" },
    { name: "Latvia", iso2: "lv", dialCode: "371", flag: "🇱🇻" },
    { name: "Liechtenstein", iso2: "li", dialCode: "423", flag: "🇱🇮" },
    { name: "Lithuania", iso2: "lt", dialCode: "370", flag: "🇱🇹" },
    { name: "Luxembourg", iso2: "lu", dialCode: "352", flag: "🇱🇺" },
    { name: "Malta", iso2: "mt", dialCode: "356", flag: "🇲🇹" },
    { name: "Moldova", iso2: "md", dialCode: "373", flag: "🇲🇩" },
    { name: "Monaco", iso2: "mc", dialCode: "377", flag: "🇲🇨" },
    { name: "Montenegro", iso2: "me", dialCode: "382", flag: "🇲🇪" },
    { name: "Netherlands", iso2: "nl", dialCode: "31", flag: "🇳🇱" },
    { name: "North Macedonia", iso2: "mk", dialCode: "389", flag: "🇲🇰" },
    { name: "Norway", iso2: "no", dialCode: "47", flag: "🇳🇴" },
    { name: "Poland", iso2: "pl", dialCode: "48", flag: "🇵🇱" },
    { name: "Portugal", iso2: "pt", dialCode: "351", flag: "🇵🇹" },
    { name: "Romania", iso2: "ro", dialCode: "40", flag: "🇷🇴" },
    { name: "San Marino", iso2: "sm", dialCode: "378", flag: "🇸🇲" },
    { name: "Serbia", iso2: "rs", dialCode: "381", flag: "🇷🇸" },
    { name: "Slovakia", iso2: "sk", dialCode: "421", flag: "🇸🇰" },
    { name: "Slovenia", iso2: "si", dialCode: "386", flag: "🇸🇮" },
    { name: "Spain", iso2: "es", dialCode: "34", flag: "🇪🇸" },
    { name: "Sweden", iso2: "se", dialCode: "46", flag: "🇸🇪" },
    { name: "Switzerland", iso2: "ch", dialCode: "41", flag: "🇨🇭" },
    { name: "Turkey", iso2: "tr", dialCode: "90", flag: "🇹🇷" },
    { name: "Ukraine", iso2: "ua", dialCode: "380", flag: "🇺🇦" },
    { name: "United Kingdom", iso2: "gb", dialCode: "44", flag: "🇬🇧" },
    { name: "Vatican City", iso2: "va", dialCode: "379", flag: "🇻🇦" }
];

const selectEl = ref()
const iconKey = ref(0)

function openSelect() {
    selectEl.value.$el.click()
}

const phoneInput = ref('')
const country = ref(defaultCountry);

const flagSrc = computed(() => country.value ? `assets/icon/flags/${country.value.iso2.toLowerCase()}.svg` : `assets/icon/globe.svg`)

function emitPhone() {
    const national = phoneInput.value.replace(/\s/g, "")
    const fullNumber = `+${country.value.dialCode}${national}`

    const parsed = parsePhoneNumberFromString(fullNumber)

    if (parsed?.isValid()) {
        emit("update:phone", parsed.format("E.164"))
        emit('valid', true) // valid
    } else {
        emit('valid', false) // invalid → empty string
    }
}

function sanitizePhone(value?: string | number): string {
    return String(value ?? '').replace(/[^0-9 ]/g, '') // remove everything except digits and spaces
}

function onPhoneInput(value?: string | number) {
    const sanitized = sanitizePhone(value)

    phoneInput.value = sanitized // update internal national number

    emitPhone() // combines with country and emits E.164
}

watch(phoneInput, emitPhone)
watch(country, emitPhone)

watch(() => props.phone, (val) => {
        if (!val) return

        const parsed = parsePhoneNumberFromString(val) || parsePhoneNumberFromString(`+${country.value.dialCode}${val}`)
        if (!parsed) return

        const found = europeanCountries.find(c => c.iso2.toUpperCase() === parsed.country)

        if (found) {
            country.value = found
        }

        iconKey.value++
        phoneInput.value = parsed.nationalNumber
    },
    { immediate: true }
)

watch(() => props.initCoutry, (iso) => {
        country.value = europeanCountries.find(c => c.iso2 === iso?.toLowerCase()) || defaultCountry
    },
    { immediate: true }
)
</script>

<style scoped>
.country-container {
    display: flex;
    align-items: center;
    gap: 5px;
    padding-left: 12px;
    border-radius: 8px 0 0 8px;
    border-right: 1px solid lightgrey;
    background-color: white;
    height: 38px;
}

.country-dropdown {
    width: max-content;
    border-radius: 5px;
    padding-right: 12px;
    --padding-start: 0;
    --ion-color-step-650: var(--color, #000);
}

.country-dropdown::part(text) {
    margin-bottom: -0.1875em;
}

.country-dropdown-icon {
    width: 20px;
    height: 20px;
    padding-top: 2px;
}

ion-select::part(icon) {
    display: none;
}
</style>

<style>
.country-dropdown .select-interface-option.item-radio-checked {
    --background: var(--ion-color-grey3)
}
.international .phone-input {
    border-radius: 0 8px 8px 0 !important;
}
.international .phone-input input {
    margin-bottom: -0.1875em;
}
</style>
